// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Tithe — one round, decoupled from Trystero, the DOM and the clock (driven by submit()/receive()/
// tick()), which is what makes the host-transfer and walkover contracts testable without a network.
//
// LOCKSTEP, AND THE DECISION THAT REMOVES A WHOLE CLASS OF HANGS: every message carries the ENTIRE
// ply log, not the latest move. A whole game is a few dozen packed ints, so the wire cost is
// nothing, and in exchange a dropped datagram becomes unobservable — the next message reconstructs
// the world. The classic lockstep failure ("peer A sent ply 11, it was lost on a turn boundary,
// both peers now wait for each other forever") cannot occur, because there is no delta to lose. A
// 2s heartbeat re-sends the same log, so even a peer that goes quiet self-heals within one beat.
//
// A received log is REPLAYED through this peer's own genPlies(). A ply that is not in the legal set
// means the two sides are running different rules — a stale cached build — and that is reported as
// exactly that, rather than silently diverging.

import {
  PH_OVER,
  PH_PLAY,
  RULES_REV,
  bearersLeft,
  digest,
  createState,
  genPlies,
  isOver,
  plyKind,
  K_MOVE,
  step,
  type PlyEvent,
  type State,
  type WinReason,
} from './rules';
import { modeOf, type Mode } from './modes';
import { chooseAny, type SearchOpts } from './bot';

export interface WireMsg {
  /** Round number, so a message from the previous round cannot disturb this one. */
  r: number;
  /** The whole ply log. */
  p: number[];
  /** FNV digest of the state after replaying it. */
  d: number;
  /** Rules revision — a mismatch is a build mismatch, not a desync. */
  v: number;
  /** The mode. Two peers on different modes is a build problem, and it must say so. */
  m: string;
}

export type DuelFault = null | 'desync' | 'build';

export interface ResultRow {
  seat: number;
  name: string;
  isSelf: boolean;
  isBot: boolean;
  won: boolean;
  turns: number;
  takes: number;
  lost: number;
  closest: number;
  threats: number;
  mobility: number;
  slack: number;
  left: boolean;
}

export interface DuelDeps {
  send: (m: WireMsg) => void;
  onChange: () => void;
  onEnd: () => void;
  onEvents: (events: PlyEvent[], s: State) => void;
}

export interface DuelOpts {
  mode: Mode;
  seed: number;
  round: number;
  names: [string, string];
  /** null = spectator. */
  mySeat: number | null;
  /** Solo only: the bot plays the other seat. */
  bot: SearchOpts | null;
  deps: DuelDeps;
  /** Per-turn clock in ms, 0 = none. Multiplayer only. */
  clockMs?: number;
}

/** Peers alternate which seat they take, so a match is fair even if one opening is not. */
export const seatForRound = (rosterIndex: number, round: number): number => (rosterIndex + round + 1) % 2;

const HOLD_MS = 12_000;

/**
 * How long the room may go silent before the survivor stops trusting `onPeerLeave`. Trystero does
 * not always report a tab that vanished abruptly, so without this the walkover never arms and the
 * survivor sits on "Waiting for…" forever with a live board. A present peer heartbeats every 2s, so
 * twenty seconds of nothing is not a slow player — it is an absent one.
 */
const SILENCE_MS = 20_000;

export class Duel {
  readonly state: State;
  readonly mode: Mode;
  readonly names: [string, string];
  readonly mySeat: number | null;
  readonly bot: SearchOpts | null;
  readonly clockMs: number;
  fault: DuelFault = null;
  /** Seat that has gone quiet, and how long is left before the walkover lands. */
  missing: number | null = null;
  holdLeft = 0;
  turnLeft = 0;
  botThinking = false;
  /** Ms since anything was heard from the other peer. Only meaningful in a networked round. */
  silence = 0;
  private readonly networked: boolean;
  private ended = false;
  /** Public so the game screen can take over the callbacks when it mounts. */
  deps: DuelDeps;
  private readonly round: number;

  constructor(o: DuelOpts) {
    this.mode = o.mode;
    this.state = createState(o.mode.id, o.seed);
    this.names = o.names;
    this.mySeat = o.mySeat;
    this.bot = o.bot;
    this.deps = o.deps;
    this.round = o.round;
    this.clockMs = o.clockMs ?? 0;
    this.turnLeft = this.clockMs;
    this.networked = o.bot === null && o.mySeat !== null;
  }

  get isMyTurn(): boolean {
    return this.mySeat !== null && this.state.turn === this.mySeat && !isOver(this.state) && this.fault === null;
  }

  /** True while the seat to move is a local bot rather than a person. */
  get isBotTurn(): boolean {
    return this.bot !== null && this.mySeat !== null && this.state.turn !== this.mySeat && !isOver(this.state);
  }

  legal(): number[] {
    if (this.fault !== null || isOver(this.state)) return [];
    return genPlies(this.state);
  }

  /** Commit a ply chosen locally (by the player, the local bot, or the turn clock). */
  submit(ply: number): boolean {
    if (isOver(this.state) || this.fault !== null) return false;
    if (!genPlies(this.state).includes(ply >>> 0)) return false;
    const events: PlyEvent[] = [];
    step(this.state, ply >>> 0, events);
    this.turnLeft = this.clockMs;
    this.deps.onEvents(events, this.state);
    this.broadcast();
    this.deps.onChange();
    this.checkEnd();
    return true;
  }

  /** Re-send the whole log. Cheap, idempotent, and the reason a lost message is not a hang. */
  broadcast(): void {
    // A spectator has no authority over anything, so it never speaks — otherwise a peer that
    // joined mid-round would broadcast an empty log and look like a desync to everybody else.
    if (this.mySeat === null) return;
    this.deps.send({ r: this.round, p: this.state.log.slice(), d: digest(this.state), v: RULES_REV, m: this.mode.id });
  }

  receive(msg: WireMsg): void {
    if (!msg || msg.r !== this.round || this.fault !== null) return;
    // Anything at all from the other side counts as "still there", including a heartbeat that
    // carries a log we already have.
    this.silence = 0;
    if (this.missing !== null) this.peerBack();
    if (msg.v !== RULES_REV || (typeof msg.m === 'string' && msg.m !== this.mode.id)) {
      this.fault = 'build';
      this.deps.onChange();
      return;
    }
    const incoming = Array.isArray(msg.p) ? msg.p : [];
    if (incoming.length <= this.state.log.length) {
      // They are behind or level. If level and we disagree, one of us is not running these rules.
      if (incoming.length === this.state.log.length && typeof msg.d === 'number' && msg.d !== digest(this.state)) {
        this.fault = 'desync';
        this.deps.onChange();
      }
      return;
    }
    for (let i = 0; i < this.state.log.length; i++) {
      if ((incoming[i] >>> 0) !== this.state.log[i]) {
        this.fault = 'desync';
        this.deps.onChange();
        return;
      }
    }
    const events: PlyEvent[] = [];
    for (let i = this.state.log.length; i < incoming.length; i++) {
      if (isOver(this.state)) break;
      const ply = incoming[i] >>> 0;
      if (!genPlies(this.state).includes(ply)) {
        this.fault = 'desync';
        this.deps.onChange();
        return;
      }
      step(this.state, ply, events);
    }
    if (typeof msg.d === 'number' && msg.d !== digest(this.state)) {
      this.fault = 'desync';
      this.deps.onChange();
      return;
    }
    this.missing = null;
    this.holdLeft = 0;
    this.turnLeft = this.clockMs;
    this.deps.onEvents(events, this.state);
    this.deps.onChange();
    this.checkEnd();
  }

  /**
   * Advance the wall clock. Everything time-based lives here and is driven by setInterval from the
   * screen — never rAF, which a backgrounded tab throttles to nothing.
   */
  tick(dtMs: number): void {
    if (isOver(this.state) || this.fault !== null) return;

    if (this.networked && this.missing === null) {
      this.silence += dtMs;
      if (this.silence >= SILENCE_MS && this.mySeat !== null) {
        this.peerLeft(1 - this.mySeat);
        return;
      }
    }

    if (this.missing !== null) {
      this.holdLeft = Math.max(0, this.holdLeft - dtMs);
      if (this.holdLeft <= 0) {
        this.walkover();
        return;
      }
      this.deps.onChange();
      return;
    }

    if (this.clockMs > 0 && this.isMyTurn) {
      this.turnLeft = Math.max(0, this.turnLeft - dtMs);
      if (this.turnLeft <= 0) {
        // Never a forfeit and never a freeze: your own peer plays your best move, so the ply comes
        // from the same place every other ply comes from and lockstep is untouched.
        const opts: SearchOpts = this.bot ?? { depth: 3, timeMs: 300 };
        const ply = chooseAny(this.state, opts);
        if (ply >= 0) this.submit(ply);
        return;
      }
      this.deps.onChange();
    }
  }

  /** The other peer vanished. Hold for a moment — a phone changing network does this. */
  peerLeft(seat: number): void {
    if (isOver(this.state) || this.mySeat === null || seat === this.mySeat) return;
    this.missing = seat;
    this.holdLeft = HOLD_MS;
    this.deps.onChange();
  }

  peerBack(): void {
    this.silence = 0;
    if (this.missing === null) return;
    this.missing = null;
    this.holdLeft = 0;
    this.broadcast();
    this.deps.onChange();
  }

  private walkover(): void {
    if (isOver(this.state) || this.mySeat === null) return;
    this.state.phase = PH_OVER;
    // A room that collapses to one survivor is a WALKOVER — the survivor wins and reaches the full
    // results screen. But a round that never got a move is NO CONTEST: awarding a win over an
    // untouched board would put a number on a tally nobody played for.
    const played = this.state.log.some((p) => plyKind(p) === K_MOVE);
    this.state.winner = played ? this.mySeat : -1;
    this.state.reason = (played ? 'walkover' : 'nocontest') as WinReason;
    this.deps.onChange();
    this.checkEnd();
  }

  private checkEnd(): void {
    if (!isOver(this.state) || this.ended) return;
    this.ended = true;
    this.deps.onEnd();
  }

  get reason(): WinReason {
    return this.state.reason;
  }

  get inPlay(): boolean {
    return this.state.phase === PH_PLAY;
  }

  /** Every player's outcome, always — never just the local one. */
  results(): ResultRow[] {
    const s = this.state;
    return [0, 1].map((seat) => {
      const st = s.stats[seat];
      return {
        seat,
        name: this.names[seat],
        isSelf: this.mySeat === seat,
        isBot: this.bot !== null && this.mySeat !== seat,
        won: s.winner === seat,
        turns: st.turns,
        takes: st.takes,
        lost: st.lost + st.levied,
        closest: st.closest,
        threats: st.threats,
        mobility: st.mobN > 0 ? st.mobSum / st.mobN : 0,
        slack: st.slack,
        left: this.missing === seat,
      };
    });
  }

  bearers(seat: number): number {
    return bearersLeft(this.state, seat);
  }
}

export function duelNames(a: string, b: string): [string, string] {
  return [a || 'Ochre', b || 'Indigo'];
}

export const modeFromOpts = (opts: { mode?: string } | null | undefined): Mode => modeOf(opts?.mode);
